import React, { Component } from "react";
import { withStyles } from "@mui/styles";
import { withTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import { toast } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";

const styles = (theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "flex-end",
  },
});

class BinMappingGeneration extends Component {
  constructor(props) {
    super(props);
    this.state = {
      colorCode: props.colorCode || "#7c3aed", // Default to orange
      codeGenerationMethod: props.codeGenerationMethod || "single",
      manualCode: props.manualCode || "",
      letterRangeStart: props.letterRangeStart || "",
      letterRangeEnd: props.letterRangeEnd || "",
      numberRangeStart: props.numberRangeStart || "",
      numberRangeEnd: props.numberRangeEnd || "",
      isGeneratingCode: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.codeGenerationMethod !== this.props.codeGenerationMethod) {
      this.setState({ codeGenerationMethod: this.props.codeGenerationMethod || "single" });
    }
  }

  handleMethodChange = (event) => {
    const codeGenerationMethod = event.target.value;
    this.setState(
      {
        codeGenerationMethod,
        manualCode: "",
        letterRangeStart: "",
        letterRangeEnd: "",
        numberRangeStart: "",
        numberRangeEnd: "",
      },
      () => {
        if (this.props.onStateChange) {
          this.props.onStateChange({
            codeGenerationMethod: this.state.codeGenerationMethod,
            manualCode: this.state.manualCode,
            letterRangeStart: this.state.letterRangeStart,
            letterRangeEnd: this.state.letterRangeEnd,
            numberRangeStart: this.state.numberRangeStart,
            numberRangeEnd: this.state.numberRangeEnd,
          });
        }
      }
    );
  };

  handleInputChange = (name) => (event) => {
    const value = event.target.value;
    this.setState(
      {
        [name]: value,
      },
      () => {
        if (this.props.onStateChange) {
          this.props.onStateChange({
            colorCode: this.state.colorCode,
            codeGenerationMethod: this.state.codeGenerationMethod,
            manualCode: this.state.manualCode,
            letterRangeStart: this.state.letterRangeStart,
            letterRangeEnd: this.state.letterRangeEnd,
            numberRangeStart: this.state.numberRangeStart,
            numberRangeEnd: this.state.numberRangeEnd,
          });
        }
      }
    );
  };

  handleColorChange = (color) => {
    this.setState(
      {
        colorCode: color,
      },
      () => {
        if (this.props.onStateChange) {
          this.props.onStateChange({
            colorCode: this.state.colorCode,
            codeGenerationMethod: this.state.codeGenerationMethod,
            manualCode: this.state.manualCode,
            letterRangeStart: this.state.letterRangeStart,
            letterRangeEnd: this.state.letterRangeEnd,
            numberRangeStart: this.state.numberRangeStart,
            numberRangeEnd: this.state.numberRangeEnd,
          });
        }
      }
    );
  };

  handleGenerateCode = async () => {
    this.setState({ isGeneratingCode: true });
    try {
      const { codeGenerationMethod, manualCode, letterRangeStart, letterRangeEnd, numberRangeStart, numberRangeEnd } = this.state;

      // Validation
      if (codeGenerationMethod === "single") {
        if (!manualCode || manualCode.trim() === "") {
          toast.error(this.props.t("Location.CodeGeneration.CodeRequired"));
          this.setState({ isGeneratingCode: false });
          return;
        }
      } else if (codeGenerationMethod === "range") {
        if (!letterRangeStart || !letterRangeEnd || !numberRangeStart || !numberRangeEnd) {
          toast.error(this.props.t("Location.CodeGeneration.RangeRequired"));
          this.setState({ isGeneratingCode: false });
          return;
        }

        // Validate range format
        if (!/^[A-Za-z]$/.test(letterRangeStart) || !/^[A-Za-z]$/.test(letterRangeEnd)) {
          toast.error(this.props.t("Location.CodeGeneration.InvalidRangeChar"));
          this.setState({ isGeneratingCode: false });
          return;
        }

        if (letterRangeStart.localeCompare(letterRangeEnd) > 0) {
          toast.error(this.props.t("Location.CodeGeneration.InvalidLetterRange"));
          this.setState({ isGeneratingCode: false });
          return;
        }

        const startNum = parseInt(numberRangeStart, 10);
        const endNum = parseInt(numberRangeEnd, 10);
        if (isNaN(startNum) || isNaN(endNum)) {
          toast.error(this.props.t("Location.CodeGeneration.InvalidNumberRange"));
          this.setState({ isGeneratingCode: false });
          return;
        }

        if (startNum < 1 || endNum < 1) {
          toast.error(this.props.t("Location.CodeGeneration.NumberRangeOutOfBounds"));
          this.setState({ isGeneratingCode: false });
          return;
        }

        if (startNum > endNum) {
          toast.error(this.props.t("Location.CodeGeneration.InvalidNumberRange"));
          this.setState({ isGeneratingCode: false });
          return;
        }
      }

      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      const codeData = {
        box_id: (this.props.box && this.props.box.id) || null,
        color_code: this.state.colorCode,
        generation_method: codeGenerationMethod,
      };

      if (codeGenerationMethod === "single") {
        codeData.manual_code = manualCode.trim();
      } else if (codeGenerationMethod === "range") {
        codeData.letter_range_start = letterRangeStart.toUpperCase();
        codeData.letter_range_end = letterRangeEnd.toUpperCase();
        codeData.number_range_start = parseInt(numberRangeStart, 10);
        codeData.number_range_end = parseInt(numberRangeEnd, 10);
      }

      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/generate-locations",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(codeData),
        }
      );

      const result = await response.json();
      if (result && result.success) {
        const pickFirstNumber = (...values) => {
          for (let i = 0; i < values.length; i++) {
            const v = values[i];
            if (v !== undefined && v !== null && v !== "") {
              return v;
            }
          }
          return 0;
        };

        const totalCreated = pickFirstNumber(
          result && result.data && result.data.total_created,
          result && result.data && result.data.totalCreated,
          result && result.data && result.data.totalCreatedCount,
          result && result.total_created,
          result && result.totalCreated,
          result && result.count,
          0
        );

        const totalSkipped = pickFirstNumber(
          result && result.data && result.data.total_skipped,
          result && result.data && result.data.totalSkipped,
          result && result.data && result.data.totalSkippedCount,
          result && result.total_skipped,
          result && result.totalSkipped,
          0
        );
        this.setState(
          {
            isGeneratingCode: false,
            manualCode: "",
            letterRangeStart: "",
            letterRangeEnd: "",
            numberRangeStart: "",
            numberRangeEnd: "",
          },
          () => {
            if (this.props.onLocationGenerated) {
              this.props.onLocationGenerated();
            }
          }
        );

        if (Number(totalCreated) <= 0 && Number(totalSkipped) > 0) {
          toast.error(
            this.props.t("Location.CodeGeneration.AlreadyExists") ||
              this.props.t("Location.CodeGeneration.SuccessMessage")
          );
        } else {
          toast.success(
            this.props.t("Location.CodeGeneration.Success", {
              count: Number(totalCreated) > 0 ? totalCreated : 1,
            }) || this.props.t("Location.CodeGeneration.SuccessMessage")
          );

          if (Number(totalSkipped) > 0) {
            toast.warning(
              this.props.t("Location.CodeGeneration.SkippedAlreadyExists", {
                count: totalSkipped,
              }) || `Skipped ${totalSkipped} location(s) (already existed).`
            );
          }
        }
      } else {
        this.setState({ isGeneratingCode: false });
        toast.error(
          result.message ||
            this.props.t("Location.CodeGeneration.Error")
        );
      }
    } catch (error) {
      this.setState({ isGeneratingCode: false });
      toast.error(this.props.t("Location.CodeGeneration.Error"));
    }
  };

  render() {
    const { classes, t, box } = this.props;
    const {
      codeGenerationMethod,
      manualCode,
      letterRangeStart,
      letterRangeEnd,
      numberRangeStart,
      numberRangeEnd,
      isGeneratingCode,
    } = this.state;

    if (!box || !(box && box.id)) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ color: "#64748b" }}>
            {t("Location.CodeGeneration.BoxIdRequired")}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3 }}>
        <ValidatorForm
          ref="codeGenerationForm"
          onSubmit={this.handleGenerateCode}
          onError={(errors) => console.log(errors)}
        >
          <FormControl component="fieldset" className={classes.formControl} fullWidth>
            <FormLabel component="legend" style={{ marginBottom: "12px", color: "#475569", fontWeight: 500 }}>
              {t("Location.CodeGeneration.ColorCode")}
            </FormLabel>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
              {[
                { color: "#f97316", name: "Orange" },
                { color: "#7c3aed", name: "Purple" },
                { color: "#ef4444", name: "Red" },
                { color: "#3b82f6", name: "Blue" },
                { color: "#10b981", name: "Green" },
              ].map((colorOption) => (
                <Box
                  key={colorOption.color}
                  onClick={() => this.handleColorChange(colorOption.color)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: colorOption.color,
                    border: this.state.colorCode === colorOption.color ? "3px solid #7c3aed" : "2px solid #e2e8f0",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: `0 4px 8px ${colorOption.color}40`,
                    },
                  }}
                >
                  {this.state.colorCode === colorOption.color && (
                    <Typography sx={{ color: "#ffffff", fontSize: "20px", fontWeight: "bold" }}>✓</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </FormControl>

          <FormControl component="fieldset" className={classes.formControl} fullWidth>
            <FormLabel
              component="legend"
              htmlFor="codeGenerationMethod"
              sx={{ marginBottom: "16px", color: "#475569", fontWeight: 500, fontSize: "0.875rem" }}
            >
              {t("Location.CodeGeneration.GenerationMethod")}
            </FormLabel>
            <RadioGroup
              row
              aria-label="codeGenerationMethod"
              name="codeGenerationMethod"
              id="codeGenerationMethod"
              value={codeGenerationMethod}
              onChange={this.handleMethodChange}
            >
              <FormControlLabel
                value="single"
                control={<Radio />}
                label={t("BinMapping.Single") || "Single"}
              />
              <FormControlLabel
                value="range"
                control={<Radio />}
                label={t("BinMapping.Bulk") || "Bulk"}
              />
            </RadioGroup>
          </FormControl>

          {codeGenerationMethod === "single" && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextValidator
                  fullWidth
                  label={t("BinMapping.EnterLocation") || "Enter Location"}
                  placeholder={t("BinMapping.EnterLocationPlaceholder") || "Enter location code"}
                  onChange={this.handleInputChange("manualCode")}
                  name="manualCode"
                  value={manualCode}
                  validators={["required"]}
                  errorMessages={[t("Location.CodeGeneration.CodeRequired")]}
                />
              </Grid>
            </Grid>
          )}

          {codeGenerationMethod === "range" && (
            <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ marginBottom: "16px", color: "#475569", fontWeight: 500, fontSize: "0.875rem" }}>
                        {t("Location.CodeGeneration.LetterRange")}
                      </Typography>
                    </Grid>
              <Grid item xs={6}>
                <TextValidator
                  fullWidth
                  label={t("Location.CodeGeneration.LetterRangeStart")}
                  onChange={this.handleInputChange("letterRangeStart")}
                  name="letterRangeStart"
                  value={letterRangeStart}
                  validators={["required", "matchRegexp:^[A-Za-z]$"]}
                  errorMessages={[
                    t("Location.CodeGeneration.LetterRangeStartRequired"),
                    t("Location.CodeGeneration.InvalidRangeChar"),
                  ]}
                  inputProps={{ maxLength: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextValidator
                  fullWidth
                  label={t("Location.CodeGeneration.LetterRangeEnd")}
                  onChange={this.handleInputChange("letterRangeEnd")}
                  name="letterRangeEnd"
                  value={letterRangeEnd}
                  validators={["required", "matchRegexp:^[A-Za-z]$"]}
                  errorMessages={[
                    t("Location.CodeGeneration.LetterRangeEndRequired"),
                    t("Location.CodeGeneration.InvalidRangeChar"),
                  ]}
                  inputProps={{ maxLength: 1 }}
                />
              </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ marginTop: "16px", marginBottom: "16px", color: "#475569", fontWeight: 500, fontSize: "0.875rem" }}>
                        {t("BinMapping.NumberRange") || "Number Range (1 or greater)"}
                      </Typography>
                    </Grid>
              <Grid item xs={6}>
                <TextValidator
                  fullWidth
                  label={t("BinMapping.NumberRangeStart") || "Number Range Start (1 or greater)"}
                  placeholder={t("BinMapping.NumberRangeStartPlaceholder") || "e.g., 1, 10, 100"}
                  onChange={this.handleInputChange("numberRangeStart")}
                  name="numberRangeStart"
                  value={numberRangeStart}
                  validators={["required", "isNumber", "minNumber:1"]}
                  errorMessages={[
                    t("Location.CodeGeneration.NumberRangeStartRequired"),
                    t("Location.CodeGeneration.NumberRangeOutOfBounds"),
                  ]}
                />
              </Grid>
              <Grid item xs={6}>
                <TextValidator
                  fullWidth
                  label={t("BinMapping.NumberRangeEnd") || "Number Range End (1 or greater)"}
                  placeholder={t("BinMapping.NumberRangeEndPlaceholder") || "e.g., 10, 100, 1000"}
                  onChange={this.handleInputChange("numberRangeEnd")}
                  name="numberRangeEnd"
                  value={numberRangeEnd}
                  validators={["required", "isNumber", "minNumber:1"]}
                  errorMessages={[
                    t("Location.CodeGeneration.NumberRangeEndRequired"),
                    t("Location.CodeGeneration.NumberRangeOutOfBounds"),
                  ]}
                />
              </Grid>
            </Grid>
          )}

          <Box className={classes.buttonContainer}>
            <Button
              type="submit"
              variant="contained"
              disabled={
                isGeneratingCode ||
                (codeGenerationMethod === "single" && !manualCode) ||
                (codeGenerationMethod === "range" &&
                  (!letterRangeStart || !letterRangeEnd || !numberRangeStart || !numberRangeEnd))
              }
              startIcon={isGeneratingCode ? <CircularProgress size={20} /> : null}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                padding: "10px 24px",
                fontSize: "0.875rem",
                fontWeight: 600,
                backgroundColor: "#7c3aed",
                boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#6d28d9",
                  boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  backgroundColor: "#cbd5e1",
                  color: "#94a3b8",
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
              }}
            >
              {isGeneratingCode
                ? t("Location.CodeGeneration.Generating")
                : t("Location.CodeGeneration.GenerateCode")}
            </Button>
          </Box>
        </ValidatorForm>
      </Box>
    );
  }
}

export default withStyles(styles)(withTranslation()(BinMappingGeneration));




// WEBPACK FOOTER //
// ./src/components/bin-mapping/BinMappingGeneration.js